import {
  Directive, EventEmitter, ElementRef, HostBinding, HostListener, Inject, Input, Output, Renderer2
} from '@angular/core';
import { AnimationBuilder, animate, style } from '@angular/animations';
import { Subscription } from 'rxjs';
import { Breakpoint, MediaQueryService } from '../../services/media-query.service';
import { WINDOW_TOKEN } from '../../services/bootstrap.service';

import type { OnDestroy, OnInit } from '@angular/core';
import type { AnimationFactory, AnimationPlayer } from '@angular/animations';
import type { Observable } from 'rxjs';

import type { BreakpointState } from '../../services/media-query.service';

export type SideSheetPositionType = 'left' | 'right' | 'top' | 'bottom';
export type SideSheetSizeType = 'full' | 'menu' | 'fullscreen';

type SideSheetPositionMap = { [key in SideSheetPositionType]: string };
type SideSheetSizeMap = { [key in SideSheetSizeType]: string };

/**
 * Directive to make a component appear as a side-sheet from left or right of viewport.
 * For usages see side-sheet.e2e.component.html
 *
 */

@Directive({
  selector: '[tdWbSideSheet]',
  exportAs: 'tdWbSideSheet'
})
export class SideSheetDirective implements OnInit, OnDestroy {
  @HostBinding('style.opacity') styleOpacity: string = '0';

  @HostBinding('style.transitionDuration.ms')
  get styleTransitionDuration(): number {
    return 600;
  }

  @Input('tdWbSideSheet') sideSheet: Observable<boolean> | undefined;

  @Input('tdWbSideSheetPosition') position: SideSheetPositionType = 'right';

  @Input('tdWbSideSheetSize') sheetSize: SideSheetSizeType = 'full';

  @Input('tdWbSideSheetBg') hasBackground: boolean = true;

  @Output('backdropClicked') backdropClickedEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  private direction: SideSheetPositionMap = {
    'left': '-100%, 0, 0',
    'right': '100%, 0, 0',
    'top': '0, -100%, 0',
    'bottom': '0, 100%, 0'
  };

  private sheetMaxWidth: SideSheetSizeMap = {
    'full': '1023px',
    'menu': '360px',
    'fullscreen': '100%'
  };

  private toggleSideSheetSubscription: Subscription = Subscription.EMPTY;
  private windowSizeSubscription: Subscription = Subscription.EMPTY;

  private cssWidthValue: string = '100%';
  private cssPositionOffsetValue: string = '0';
  private cssPositionValue: string = 'fixed';
  private cssHeightValue: string = '100vh';
  private transformValue: string | undefined;
  private maxWidthValue: string | undefined;
  private zIndexValue: string = '600';

  private sideSheetBackground: HTMLElement;
  private sideSheetParent: HTMLElement;
  private triggerReflow: number;
  private fadeOutAnimation: AnimationFactory;
  private fadeInAnimation: AnimationFactory;

  constructor(@Inject(ElementRef) private element: ElementRef,
              @Inject(WINDOW_TOKEN) private browserWindow: Window,
              @Inject(Renderer2) private renderer: Renderer2,
              @Inject(MediaQueryService) private mediaQueryService: MediaQueryService,
              @Inject(AnimationBuilder) private animationBuilder: AnimationBuilder) {
  }

  @HostListener('click', ['$event']) clicked(event: MouseEvent): void {
    if (event && event.target === this.element.nativeElement) {
      this.backdropClickedEvent.emit(event);
    }
  }

  ngOnInit(): void {
    this.initAnimations();
    this.transformValue = `translate3d(${this.direction[this.position]})`;
    this.maxWidthValue = this.sheetMaxWidth[this.sheetSize];
    this.subscribeToToggleStyleSheet();
    this.setHostStyles();

    this.windowSizeSubscription = this.mediaQueryService.observe(Breakpoint.SM).subscribe(
      (state: BreakpointState): void => this.onScreenChange(state.matches));
  }

  ngOnDestroy(): void {
    if (this.toggleSideSheetSubscription !== undefined) {
      this.toggleSideSheetSubscription.unsubscribe();
    }
    if (this.windowSizeSubscription !== undefined) {
      this.windowSizeSubscription.unsubscribe();
    }
  }

  private initAnimations(): void {
    this.fadeOutAnimation = this.animationBuilder.build([
      style({ background: 'rgba(0, 0, 0, 0.32)' }),
      animate(600, style({ background: 'rgba(0, 0, 0, 0)' }))
    ]);
    this.fadeInAnimation = this.animationBuilder.build([
      style({ background: 'rgba(0, 0, 0, 0)' }),
      animate(600, style({ background: 'rgba(0, 0, 0, 0.32)' }))
    ]);
  }

  private subscribeToToggleStyleSheet(): void {
    if (this.sideSheet !== undefined) {
      const element: HTMLElement = this.element.nativeElement;
      this.toggleSideSheetSubscription = this.sideSheet.subscribe({
        next: (show: boolean): void => {
          if (show) {
            this.renderer.setStyle(element, 'display', 'flex');
            this.triggerReflow = this.browserWindow.scrollY; // This is here to make possible animation after changing display value!
            this.styleOpacity = '1';
            this.renderer.setStyle(element, 'transform', 'translate3d(0, 0, 0)');
            if (this.hasBackground) {
              this.addSideSheetBg();
            }
            this.renderer.setAttribute(element, 'aria-hidden', 'false');
          } else {
            this.renderer.setStyle(element, 'display', 'none');
            this.styleOpacity = '0';
            this.renderer.setStyle(element, 'transform', this.transformValue);
            if (this.hasBackground) {
              this.removeSideSheetBg();
            }
            if (this.triggerReflow !== undefined) {
              this.triggerReflow = undefined;
            }
            this.renderer.setAttribute(element, 'aria-hidden', 'true');
          }
        }
      });
    }
  }

  private addSideSheetBg(): void {

    const bgStyle = `
                  z-index: 600;
                  top: 0;
                  position: fixed;
                  width: 100%;
                  height: 100%;
                  left: 0;
                  opacity: 1;
          `;

    this.sideSheetBackground = this.renderer.createElement('div');
    this.sideSheetParent = this.element.nativeElement.parentNode;
    this.renderer.addClass(this.sideSheetBackground, 'td-wb-side-sheet__background');
    this.renderer.setAttribute(this.sideSheetBackground, 'style', bgStyle);
    this.renderer.insertBefore(this.sideSheetParent, this.sideSheetBackground, this.element.nativeElement);

    const loaderFadeInAnimationPlayer: AnimationPlayer = this.fadeInAnimation.create(this.sideSheetBackground);
    loaderFadeInAnimationPlayer.play();
  }

  private removeSideSheetBg(): void {
    const loaderFadeOutAnimationPlayer: AnimationPlayer = this.fadeOutAnimation.create(this.sideSheetBackground);
    loaderFadeOutAnimationPlayer.onDone(() => this.sideSheetBackground.remove());
    loaderFadeOutAnimationPlayer.play();
  }

  private setHostStyles(): void {
    const element: HTMLElement = this.element.nativeElement;
    this.renderer.setStyle(element, 'display', 'none');
    this.renderer.setStyle(element, 'width', this.cssWidthValue);
    this.renderer.setStyle(element, 'z-index', this.zIndexValue);
    this.renderer.setStyle(element, 'max-width', this.maxWidthValue);
    this.renderer.setStyle(element, 'position', this.cssPositionValue);
    this.renderer.setStyle(element, 'transform', this.transformValue);
    this.renderer.setStyle(element, 'min-height', this.cssHeightValue);
    this.renderer.setStyle(element, this.position, this.cssPositionOffsetValue);

    if (this.position === 'bottom') {
      this.renderer.setStyle(element, 'left', this.cssPositionOffsetValue);
      this.renderer.setStyle(element, 'justify-content', 'center');
    } else {
      this.renderer.setStyle(element, 'top', this.cssPositionOffsetValue);
    }
  }

  private onScreenChange(small: boolean): void {
    if (small) {
      this.maxWidthValue = this.cssWidthValue;
    } else {
      this.maxWidthValue = this.sheetMaxWidth[this.sheetSize];
    }
    this.renderer.setStyle(this.element.nativeElement, 'max-width', this.maxWidthValue);
  }
}
